// 旧アセットは配信時に保持するが、過去に削除されたchunk等への復旧手段も用意する。
// Web専用。ネイティブはバンドルを同梱するためindex.tsxで対象外にする。
// 同じ障害でのループを防ぎ、次のデプロイでも救済できるよう60秒の間隔を置く。
export const CHUNK_LOAD_ERROR_RELOAD_STORAGE_KEY = "CHUNK_LOAD_ERROR_RELOADED_V1";
const CHUNK_LOAD_ERROR_RELOAD_COOLDOWN_MS = 60000;

// 限定した部分一致のみで判定する（すべて小文字で比較）。
// "unexpected token '<'" は JSON パース失敗など無関係なエラーまで巻き込むため、意図的に含めない。
const CHUNK_LOAD_ERROR_MESSAGE_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module", // Vite / ブラウザ差分
  "importing a module script failed", // Safari
  "is not a valid javascript mime type",
  "unable to preload css for",
];

export const isChunkLoadErrorMessage = (message: unknown): boolean => {
  const text =
    typeof message === "string"
      ? message
      : message && typeof message === "object" && "message" in message
        ? (message as { message?: unknown }).message
        : undefined;
  if (typeof text !== "string" || text === "") return false;
  const lowerText = text.toLowerCase();
  return CHUNK_LOAD_ERROR_MESSAGE_PATTERNS.some((pattern) => lowerText.includes(pattern));
};

// リロードした場合はtrue。直近の復旧試行・確認キャンセル・storage使用不可ならfalse。
export const reloadOnceForChunkLoadError = (target: Window = window, hasEditedInputs = false): boolean => {
  try {
    const previous = target.sessionStorage.getItem(CHUNK_LOAD_ERROR_RELOAD_STORAGE_KEY);
    const now = Date.now();
    const elapsed = now - Number(previous);
    if (previous !== null && elapsed >= 0 && elapsed < CHUNK_LOAD_ERROR_RELOAD_COOLDOWN_MS) return false;
    // 確認をキャンセルした場合も、同じ失敗の二重通知で確認を重ねない。
    target.sessionStorage.setItem(CHUNK_LOAD_ERROR_RELOAD_STORAGE_KEY, String(now));
    if (
      hasEditedInputs &&
      !target.confirm("画面を読み込めませんでした。再読み込みしますか？入力中の内容は失われる場合があります。")
    ) return false;
  } catch {
    // プライベートブラウジング等で storage が使えない場合は、ループ防止のためリロードしない
    return false;
  }
  target.location.reload();
  return true;
};

const extractReasonMessage = (reason: unknown): unknown => {
  if (typeof reason === "string") return reason;
  if (reason && typeof reason === "object") return (reason as { message?: unknown }).message;
  return undefined;
};

export const registerChunkLoadErrorReload = (target: Window = window): void => {
  // 多段フォームの前のステップなど、DOMから消えた入力も未保存の可能性がある。
  // 保存・reset済みを推測せず、そのページで入力操作があった場合は保守的に確認する。
  let hasEditedInputs = false;
  const onInput = (event: Event) => {
    const field = event.target instanceof Element
      ? event.target.closest("input, textarea, select, [contenteditable='true']")
      : null;
    if (field) hasEditedInputs = true;
  };
  target.addEventListener("input", onInput, true);
  target.addEventListener("change", onInput, true);

  const recover = (reason: unknown): boolean => {
    if (!isChunkLoadErrorMessage(reason)) return false;
    return reloadOnceForChunkLoadError(target, hasEditedInputs);
  };

  // vite:preloadError は Vite が module preload 失敗時に発火する。リロードする場合のみ二重にエラーを投げさせない。
  const onPreloadError = (event: Event) => {
    if (recover((event as Event & { payload?: unknown }).payload)) event.preventDefault();
  };
  target.addEventListener("vite:preloadError", onPreloadError as EventListener);

  // Sentry にも記録を残したいので preventDefault は呼ばない。
  const onUnhandledRejection = (event: Event) => {
    const reason = (event as Event & { reason?: unknown }).reason;
    recover(extractReasonMessage(reason));
  };
  target.addEventListener("unhandledrejection", onUnhandledRejection as EventListener);
};
